import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext"; // Adjust the import path as necessary
import { CircularProgress } from '@mui/material';

// Optional: Import a loading indicator component if needed
// import { CircularProgress } from '@mui/material';

/**
 * A component to protect routes based on authentication status and user roles/authorities.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Use if wrapping a component directly.
 * @param {string[]} [props.roles] - Optional array of role/authority strings required to access the route.
 * Matches against the 'user.role' field from AuthContext by default.
 */
const ProtectedRoute = ({ children, roles }) => {
    // Get authentication state and user data from the context
    const { isAuthenticated, user, isLoading } = useAuth();
    const location = useLocation(); // Get current location for redirect state

    // 1. Show loading indicator while AuthContext is initializing (optional but good practice)
    if (isLoading) {
        // You can return null or a dedicated loading component
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                {/* Replace with your preferred loading indicator */}
                <CircularProgress />
                {/* <p>Loading...</p> */}
            </div>
        );
    }

    // 2. Check if the user is authenticated
    if (!isAuthenticated && !isLoading) {
        // User is not logged in, redirect to login page.
        // Include the current location (`state={{ from: location }}`)
        // so the user can be redirected back after successful login.
        console.log('ProtectedRoute: Not authenticated, redirecting to login.');
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 3. Check if specific roles/authorities are required for this route
    if (roles && roles.length > 0) {
        // Roles are required, check if the authenticated user has at least one of them.
        // --- Adjust this logic based on your user object structure ---
        const userRole = user?.role; // Assumes user object has a 'role' field (string)

        // If user object has an array of authorities instead (e.g., user.authorities = ["ADMIN", "APPROVER"])
        // const userAuthorities = user?.authorities || [];
        // const hasRequiredRole = roles.some(requiredRole => userAuthorities.includes(requiredRole));

        // Current check assuming user.role is a single string like "ADMIN"
        const hasRequiredRole = userRole && roles.includes(userRole);
        // --- End of logic to adjust ---

        if (!hasRequiredRole) {
            // User is logged in but doesn't have the required role/authority.
            // Redirect to an "Unauthorized" page or a default page like the dashboard.
            // Avoid redirecting back to login, as they *are* logged in.
            console.warn(`ProtectedRoute: Authorization failed. User role '${userRole}' not in required roles [${roles.join(', ')}] for path ${location.pathname}`);
            return <Navigate to="/unauthorized" replace />; // Create a simple page for /unauthorized
            // Or redirect to dashboard: return <Navigate to="/dashboard" replace />;
        }
    }

    // 4. User is authenticated and authorized (either no specific roles required or user has them)
    // Render the child component passed to ProtectedRoute, or Outlet if used for nested routes.
    return children ? children : <Outlet />;
};

export default ProtectedRoute;